import type { AddOnId, DemoState, Material } from '../state/store.js'

export interface AddOn {
  id: AddOnId
  title: string
  description: string
  priceUSD: number
  iconKey: 'trash' | 'shield' | 'wrench' | 'calendar'
  recommended: boolean
  reason: string | null
}

const HEAVY_MATERIALS: readonly Material[] = ['Granite', 'Marble', 'Quartz', 'Concrete'] as const

interface CatalogEntry {
  title: string
  description: string
  priceUSD: number
  iconKey: AddOn['iconKey']
}

export const ADDON_CATALOG: Record<AddOnId, CatalogEntry> = {
  haulAway: {
    title: 'Haul-Away',
    description: "We'll remove and responsibly recycle your old dishwasher",
    priceUSD: 25,
    iconKey: 'trash',
  },
  protectionPlan: {
    title: 'Protection Plan',
    description: 'Covers mechanical & electrical failures beyond warranty',
    priceUSD: 48,
    iconKey: 'shield',
  },
  customMountingKit: {
    title: 'Custom Mounting Kit',
    description: 'Brackets and hardware for stone or thick countertops',
    priceUSD: 35,
    iconKey: 'wrench',
  },
  annualMaintenance: {
    title: 'Annual maintenance plan',
    description: 'Yearly inspection and tune-up to keep your appliance running smoothly',
    priceUSD: 89,
    iconKey: 'calendar',
  },
}

interface Rule {
  addon: AddOnId
  test: (s: DemoState) => boolean
  reason: (s: DemoState) => string
}

const RULES: Rule[] = [
  {
    addon: 'customMountingKit',
    test: s => s.countertop != null && HEAVY_MATERIALS.includes(s.countertop.material),
    reason: s => `Suggested for ${s.countertop?.material.toLowerCase()} countertops`,
  },
  {
    addon: 'haulAway',
    test: s => s.applianceStatus === 'still-installed',
    reason: () => 'Suggested because your existing dishwasher is still installed',
  },
  {
    addon: 'protectionPlan',
    test: () => true,
    reason: () => 'Recommended for all installs',
  },
  {
    addon: 'annualMaintenance',
    test: () => true,
    reason: () => 'Keeps the appliance covered year-round',
  },
]

export interface AddOnsResult {
  recommended: AddOnId[]
  all: AddOn[]
}

export const computeAddOns = (state: DemoState): AddOnsResult => {
  const fired: Partial<Record<AddOnId, string>> = {}
  for (const rule of RULES) {
    if (rule.test(state)) fired[rule.addon] = rule.reason(state)
  }

  const all: AddOn[] = (Object.keys(ADDON_CATALOG) as AddOnId[]).map(id => {
    const entry = ADDON_CATALOG[id]
    const reason = fired[id] ?? null
    return { id, ...entry, recommended: reason != null, reason }
  })

  const recommended = all.filter(a => a.recommended).map(a => a.id)
  return { recommended, all }
}
